import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { registerTenant } from '../services/tenantService';

const TENANT_TYPES = [
  { value: 'band',         label: 'Band Council' },
  { value: 'first_nation', label: 'First Nation' },
  { value: 'government',   label: 'Government Agency' },
  { value: 'municipality', label: 'Municipality' },
  { value: 'organization', label: 'Organization / NGO' },
  { value: 'public',       label: 'Public Community' },
];

const INITIAL_FORM = {
  // Organization
  name:        '',
  slug:        '',
  type:        'organization',
  description: '',
  // Primary contact at the organization
  primaryContact: {
    name:             '',
    email:            '',
    phone:            '',
    organizationRole: '',
  },
  // Admin account to create
  adminFirstName: '',
  adminLastName:  '',
  adminEmail:     '',
  adminPassword:  '',
  adminConfirm:   '',
};

/**
 * TenantRegistryPage — public self-service community onboarding.
 *
 * Submits a pending tenant registration + creates an admin user account.
 * A CIP platform admin reviews and approves before the tenant goes live.
 *
 * Route: /register-community (no auth required)
 */
const TenantRegistryPage = () => {
  const [form, setForm]       = useState(INITIAL_FORM);
  const [msg, setMsg]         = useState('');
  const [submitting, setSub]  = useState(false);
  const [submitted, setSub2]  = useState(false);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const setContact = (field, value) =>
    setForm((prev) => ({ ...prev, primaryContact: { ...prev.primaryContact, [field]: value } }));

  // Auto-generate slug from org name
  const handleNameChange = (e) => {
    const name = e.target.value;
    const autoSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    set('name', name);
    if (!form.slug || form.slug === slugify(form.name)) {
      set('slug', autoSlug);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');

    if (form.adminPassword.length < 8) {
      setMsg('Admin password must be at least 8 characters.');
      return;
    }
    if (form.adminPassword !== form.adminConfirm) {
      setMsg('Admin passwords do not match.');
      return;
    }

    setSub(true);
    try {
      await registerTenant({
        name:           form.name,
        slug:           form.slug,
        type:           form.type,
        description:    form.description,
        primaryContact: form.primaryContact,
        adminFirstName: form.adminFirstName,
        adminLastName:  form.adminLastName,
        adminEmail:     form.adminEmail,
        adminPassword:  form.adminPassword,
      });
      setSub2(true);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSub(false);
    }
  };

  if (submitted) {
    return (
      <div className="page registry-page registry-page--success">
        <div className="registry-success">
          <h2>Registration Submitted</h2>
          <p>
            Thank you for registering <strong>{form.name}</strong> with the Civic Intelligence
            Platform. A CIP administrator will review your request and reach out to{' '}
            <strong>{form.primaryContact.email || form.adminEmail}</strong> within a few business
            days.
          </p>
          <p>
            Once approved, you'll be able to log in at{' '}
            <Link to="/login">civiciq.ca/login</Link> using the admin credentials you provided.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page registry-page">
      <div className="registry-header">
        <h2>Register Your Community</h2>
        <p>
          Get your organization set up on the Civic Intelligence Platform. Fill out the form below
          and a CIP administrator will review and approve your registration.
        </p>
        <p className="registry-login-hint">
          Already registered? <Link to="/login">Sign in</Link>
        </p>
      </div>

      <form className="registry-form" onSubmit={handleSubmit} noValidate>

        {/* ── Organization info ── */}
        <fieldset>
          <legend>Organization Details</legend>

          <label>
            Organization Name <span aria-hidden="true">*</span>
            <input
              type="text"
              value={form.name}
              onChange={handleNameChange}
              required
              placeholder="e.g. Matawa First Nations"
            />
          </label>

          <label>
            URL Key (slug) <span aria-hidden="true">*</span>
            <div className="slug-preview">
              <span className="slug-prefix">civiciq.ca/t/</span>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                required
                placeholder="matawa-first-nations"
                pattern="[a-z0-9-]+"
              />
            </div>
            <small>Lowercase letters, numbers, and hyphens only. This cannot be changed later.</small>
          </label>

          <label>
            Organization Type <span aria-hidden="true">*</span>
            <select value={form.type} onChange={(e) => set('type', e.target.value)} required>
              {TENANT_TYPES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <label>
            Description
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
              placeholder="Brief description of your organization and its mandate."
            />
          </label>
        </fieldset>

        {/* ── Primary contact ── */}
        <fieldset>
          <legend>Primary Contact</legend>
          <p className="fieldset-hint">Who should CIP reach out to about this registration?</p>

          <label>
            Contact Name <span aria-hidden="true">*</span>
            <input
              type="text"
              value={form.primaryContact.name}
              onChange={(e) => setContact('name', e.target.value)}
              required
              placeholder="Full name"
            />
          </label>
          <label>
            Contact Email <span aria-hidden="true">*</span>
            <input
              type="email"
              value={form.primaryContact.email}
              onChange={(e) => setContact('email', e.target.value)}
              required
            />
          </label>
          <label>
            Contact Phone
            <input
              type="tel"
              value={form.primaryContact.phone}
              onChange={(e) => setContact('phone', e.target.value)}
            />
          </label>
          <label>
            Role / Title
            <input
              type="text"
              value={form.primaryContact.organizationRole}
              onChange={(e) => setContact('organizationRole', e.target.value)}
              placeholder="e.g. Executive Director"
            />
          </label>
        </fieldset>

        {/* ── Admin account ── */}
        <fieldset>
          <legend>Administrator Account</legend>
          <p className="fieldset-hint">
            This account will be the initial community administrator. You'll use it to log in and
            manage your community once approved.
          </p>

          <div className="form-row">
            <label>
              First Name <span aria-hidden="true">*</span>
              <input
                type="text"
                value={form.adminFirstName}
                onChange={(e) => set('adminFirstName', e.target.value)}
                required
              />
            </label>
            <label>
              Last Name <span aria-hidden="true">*</span>
              <input
                type="text"
                value={form.adminLastName}
                onChange={(e) => set('adminLastName', e.target.value)}
                required
              />
            </label>
          </div>

          <label>
            Admin Email <span aria-hidden="true">*</span>
            <input
              type="email"
              value={form.adminEmail}
              onChange={(e) => set('adminEmail', e.target.value)}
              required
            />
          </label>
          <label>
            Password <span aria-hidden="true">*</span>
            <input
              type="password"
              value={form.adminPassword}
              onChange={(e) => set('adminPassword', e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <small>Minimum 8 characters.</small>
          </label>
          <label>
            Confirm Password <span aria-hidden="true">*</span>
            <input
              type="password"
              value={form.adminConfirm}
              onChange={(e) => set('adminConfirm', e.target.value)}
              required
              autoComplete="new-password"
            />
          </label>
        </fieldset>

        {msg && <p className="error-msg" role="alert">{msg}</p>}

        <div className="form-actions">
          <button type="submit" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Registration'}
          </button>
          <Link to="/" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

// helper used during name-change auto-slug
function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default TenantRegistryPage;
